// Phase 6 · the WhatsApp tracking assistant's records (08, 15 §15.4). Server-written; no client reads them.
//
//   whatsappInbound/{messageId}     every message received, stored once (the provider may deliver twice)
//   whatsappContacts/{customerUid}  consent (opt-in, STOP), the 24-hour window and what the assistant
//                                   is waiting for from this person (a rating, a comment, four digits)
import { z } from 'zod';
import { e164Schema, trackingNoSchema } from './cargo';
import { customerUidSchema, ratingSchema } from './customer';

const iso = z.string();

export const ASSISTANT_INTENTS = [
  'tracking',
  'last4',
  'rating',
  'comment',
  'agent',
  'stop',
  'start',
  'status',
  'rates',
  'book',
  'season',
  'followup_yes',
  'followup_no',
  'help',
  'unknown',
] as const;

/** What the assistant is waiting for from this person next. */
export const pendingStateSchema = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('rating'),
      ratingKind: z.enum(['collection', 'delivery', 'service']),
      trackingNo: trackingNoSchema,
      role: z.enum(['shipper', 'consignee']),
      requestedAt: iso,
    })
    .strict(),
  z
    .object({
      kind: z.literal('comment'),
      feedbackId: z.string(),
      ticketId: z.string().optional(),
      stars: ratingSchema,
      at: iso,
    })
    .strict(),
  z.object({ kind: z.literal('last4'), trackingNo: trackingNoSchema, at: iso }).strict(),
  z.object({ kind: z.literal('ticket_followup'), ticketId: z.string(), at: iso }).strict(),
]);
export type PendingState = z.infer<typeof pendingStateSchema>;

/** `whatsappContacts/{customerUid}` */
export const whatsappContactDocSchema = z
  .object({
    customerUid: customerUidSchema,
    phoneE164: e164Schema,
    language: z.enum(['en', 'tl']),
    optedOut: z.boolean(),
    optedOutAt: iso.optional(),
    optInAt: iso.optional(),
    optInSource: z.enum(['app', 'booking', 'counter', 'inbound']).optional(),
    /** The last time this person wrote to us: free-text replies are allowed for 24 hours after it. */
    lastInboundAt: iso.optional(),
    pending: pendingStateSchema.optional(),
    updatedAt: iso,
  })
  .strict();
export type WhatsappContactDoc = z.infer<typeof whatsappContactDocSchema>;

/** `whatsappInbound/{messageId}` (the provider's id, made safe for a document name). */
export const whatsappInboundDocSchema = z
  .object({
    id: z.string(),
    providerMessageId: z.string(),
    source: z.enum(['meta', 'simulator']),
    from: e164Schema,
    customerUid: customerUidSchema,
    profileName: z.string().max(120).optional(),
    type: z.string().max(40),
    text: z.string().max(4000),
    providerTimestamp: iso.optional(),
    receivedAt: iso,
    status: z.enum(['received', 'handled', 'ignored', 'failed']),
    intent: z.enum(ASSISTANT_INTENTS).optional(),
    replyOutboxIds: z.array(z.string()).max(5).default([]),
    error: z.string().max(300).optional(),
  })
  .strict();
export type WhatsappInboundDoc = z.infer<typeof whatsappInboundDocSchema>;

/** The dev callable `simulateWhatsappInbound {from, text}` (local and staging only, `dev.tools`). */
export const simulateWhatsappInboundInputSchema = z
  .object({
    from: e164Schema,
    text: z.string().trim().min(1).max(1000),
    /** Send the same id twice to see the message handled once. */
    messageId: z
      .string()
      .regex(/^[A-Za-z0-9._=-]{6,100}$/)
      .optional(),
    profileName: z.string().max(120).optional(),
  })
  .strict();
export const simulateWhatsappInboundResponseSchema = z
  .object({
    ok: z.literal(true),
    duplicate: z.boolean(),
    intent: z.enum(ASSISTANT_INTENTS).nullable(),
    /** The reply texts, as they went to the test inbox. */
    replies: z.array(z.object({ id: z.string(), text: z.string() }).strict()),
  })
  .strict();
